// src/pages/DeleteAccount.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiDeleteAccount } from '../services/api';
import styles from './Profile.module.css';

const REASONS = [
  { id: 'too_expensive',    label: 'Too expensive' },
  { id: 'found_alternative', label: 'Found an alternative' },
  { id: 'not_useful',       label: 'Not useful for me' },
  { id: 'other',            label: 'Other' },
];

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [reason, setReason]         = useState('');
  const [reasonText, setReasonText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [error, setError]           = useState('');

  const canSubmit = reason && (reason !== 'other' || reasonText.trim().length > 0);

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await apiDeleteAccount(reason, reason === 'other' ? reasonText.trim() : null);
      await logout();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Could not delete account. Please try again.');
      setShowConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button className={styles.logoBtn} onClick={() => navigate('/')} type="button">
          <span className={styles.logoBtnIcon}>D</span>
          <span className={styles.logoBtnText}>Delefy <span className={styles.logoBtnVersion}>v1.0</span></span>
        </button>
        <button className={styles.navBtn} onClick={() => navigate('/profile')} type="button">
          Back to Profile
        </button>
      </header>

      <main className={styles.mainNarrow}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Delete Your Account</h2>
          <p className={styles.sectionDesc}>
            We're sorry to see you go. Before you leave, tell us why. It helps us improve.
          </p>
        </div>

        <div className={styles.warningBanner}>
          This will permanently delete your account, and you will immediately lose all unused bill credits without refund.
          Your data cannot be recovered once this process completes.
        </div>

        <div className={styles.detailsCard}>
          <div className={styles.reasonList}>
            {REASONS.map(r => (
              <label key={r.id} className={`${styles.reasonOption} ${reason === r.id ? styles.reasonOptionActive : ''}`}>
                <input
                  type="radio"
                  name="delete_reason"
                  checked={reason === r.id}
                  onChange={() => setReason(r.id)}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>

          {reason === 'other' && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Tell us more</label>
              <textarea
                className={styles.textarea}
                value={reasonText}
                onChange={e => setReasonText(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="What made you want to leave?"
              />
            </div>
          )}

          {error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.detailsActions}>
            <button
              className={styles.deleteBtn}
              onClick={() => setShowConfirm(true)}
              disabled={!canSubmit}
              type="button"
            >
              Delete Account Permanently
            </button>
            <button className={styles.actionBtn} onClick={() => navigate('/profile')} type="button">
              Cancel
            </button>
          </div>
        </div>
      </main>

      {showConfirm && (
        <div className={styles.modalOverlay} onClick={() => !deleting && setShowConfirm(false)}>
          <div className={styles.modalCard} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Are you sure?</h3>
            <p className={styles.modalBody}>
              Are you sure you want to delete your account permanently? This cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.actionBtn}
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                type="button"
              >
                No, keep my account
              </button>
              <button
                className={styles.deleteBtn}
                onClick={handleDelete}
                disabled={deleting}
                type="button"
              >
                {deleting ? <span className={styles.spinnerSm} /> : null}
                {deleting ? 'Deleting...' : 'Yes, delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}